import React, { lazy, Suspense, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import Index from "./pages/Index";
import ProductListing from "./pages/ProductListing";
import ProductDetail from "./pages/ProductDetail";
import Bundles from "./pages/Bundles";
import BundleDetail from "./pages/BundleDetail";
import Cart from "./pages/Cart";
import OrderHistory from "./pages/OrderHistory";
import OrderDetails from "./pages/OrderDetails";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import NotFound from "./pages/NotFound";
import Settings from "./pages/Settings";
import AuthAction from "./pages/AuthAction";
import Unsubscribe from "./pages/Unsubscribe";
import AffiliateSignup from "./pages/AffiliateSignup";
import AffiliateDashboard from "./pages/AffiliateDashboard";
import RushdPricing from "./pages/RushdPricing";
import RushdSuccess from "./pages/RushdSuccess";
import RushdCancelled from "./pages/RushdCancelled";
import { useScrollToTop } from "./hooks/useScrollToTop";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import { ProductProvider } from "./context/ProductContext";
import { CartProvider } from "./context/CartContext";
import { UserProfileProvider } from "./context/UserProfileContext";
import { BannerProvider } from "./context/BannerContext";
import { ShippingProvider } from "./context/ShippingContext";
import { ShippingAddressProvider } from "./context/ShippingAddressContext";
import { AffiliateProvider } from "./context/AffiliateContext";
import { RushdMembershipProvider } from "./context/RushdMembershipContext";
import { RushdSettingsProvider } from "./context/RushdSettingsContext";
import { useAuthContext } from "@/lib/AuthProvider";
import { useBanner } from "@/context/BannerContext";
import LoadingSpinner from "./components/LoadingSpinner";
import MaintenanceMode from "./components/MaintenanceMode";
import MobileBottomNav from "./components/MobileBottomNav";
import WhatsAppFloatingButton from "./components/WhatsAppFloatingButton";
import MobileStickyHamburger from "./components/MobileStickyHamburger";
import LazyLoadErrorBoundary from "./components/LazyLoadErrorBoundary";
import LaunchSplashModal from "./components/LaunchSplashModal";
import { subscribeToMaintenanceConfig, MaintenanceConfig } from "./services/maintenanceService";
import { processReferralFromURL } from "./services/referralTrackingService";
import { initializeMobileIntegration } from "./lib/mobileAppInit";
import { useIsMobile } from "./hooks/use-mobile";
import MobileAppCtaNotice from "./components/MobileAppCtaNotice";
import GoogleAdsConsent from "./components/GoogleAdsConsent";

// Lazy load components
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 min-h-[60vh]">
    <LoadingSpinner size="lg" />
    <p className="mt-4 text-gray-500">Loading page...</p>
  </div>
);

// ScrollToTop component to be used inside the Router
const ScrollToTop = () => {
  useScrollToTop();
  return null;
};

// Layout component that includes common elements like NavBar
const Layout = () => {
  const { bannerConfig, isBannerDismissed } = useBanner();
  const location = useLocation();
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Shared mobile menu state
  const isMobile = useIsMobile();

  // Close mobile menu when navigating to different pages
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Listen for custom events from NavBar about height changes
  useEffect(() => {
    const handleNavHeightChange = (event) => {
      setTotalNavHeight(event.detail.height);
      
      // Set CSS custom property for mobile browsers
      document.documentElement.style.setProperty('--mobile-nav-height', `${event.detail.height}px`);
    };

    // Listen for height updates from NavBar
    window.addEventListener('navHeightUpdate', handleNavHeightChange);

    // Set initial CSS custom property
    document.documentElement.style.setProperty('--mobile-nav-height', `${totalNavHeight}px`);

    return () => {
      window.removeEventListener('navHeightUpdate', handleNavHeightChange);
    };
  }, [totalNavHeight]);

  return (
    <>
      <WhatsAppFloatingButton />
      <NavBar 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      <MobileStickyHamburger 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <div 
        className={`min-h-screen relative ${isMobile ? 'pb-16' : 'pb-0'}`}
        style={{ 
          paddingTop: `${totalNavHeight}px`
        }}
      >
        <Outlet />
      </div>
      {!isMobile && (
        <Footer />
      )}
      <MobileBottomNav />
      <MobileAppCtaNotice />
    </>
  );
};

// Main App component with maintenance mode checking
const AppContent = () => {
  const [maintenanceConfig, setMaintenanceConfig] = useState<MaintenanceConfig>({
    enabled: false,
    message: 'We are currently performing scheduled maintenance. Please check back soon.',
    estimatedDuration: 'a few minutes',
    lastUpdated: new Date()
  });
  const [isLoadingMaintenance, setIsLoadingMaintenance] = useState(true);

  // Subscribe to maintenance mode changes
  useEffect(() => {
    const unsubscribe = subscribeToMaintenanceConfig((config) => {
      setMaintenanceConfig(config);
      setIsLoadingMaintenance(false);
    });

    return () => unsubscribe();
  }, []);

  // Process referral tracking on app load
  useEffect(() => {
    processReferralFromURL().catch(() => {
      // Silent error handling
    });
  }, []);

  // Initialize mobile integration features
  useEffect(() => {
    try {
      initializeMobileIntegration();
    } catch (error) {
      // Silent error handling
    }
  }, []);

  // Show loading while checking maintenance status
  if (isLoadingMaintenance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show maintenance mode if enabled
  if (maintenanceConfig.enabled) {
    return (
      <MaintenanceMode 
        message={maintenanceConfig.message}
        estimatedDuration={maintenanceConfig.estimatedDuration}
      />
    );
  }

  // Normal app content
  return (
    <BannerProvider>
      <ShippingProvider>
        <ShippingAddressProvider>
          <ProductProvider>
            <CartProvider>
              <UserProfileProvider>
                <RushdSettingsProvider>
                  <RushdMembershipProvider>
                  <AffiliateProvider>
                  <Toaster />
                  <Sonner />
                  <LaunchSplashModal />
                  <ScrollToTop />
                  <Routes>
                  <Route element={<Layout />}>
                    <Route path="/" element={<Index />} />
                    <Route path="/products" element={<ProductListing />} />
                    <Route path="/products/:category" element={<ProductListing />} />
                    <Route path="/product/:productId" element={<ProductDetail />} />
                    <Route path="/bundles" element={<Bundles />} />
                    <Route path="/bundle/:slug" element={<BundleDetail />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={
                      <LazyLoadErrorBoundary fallbackPath="/cart">
                        <Suspense fallback={<PageLoadingFallback />}>
                          <ProtectedRoute>
                          <Checkout />
                          </ProtectedRoute>
                        </Suspense>
                      </LazyLoadErrorBoundary>
                    } />
                    <Route path="/order-confirmation" element={
                      <LazyLoadErrorBoundary fallbackPath="/orders">
                        <Suspense fallback={<PageLoadingFallback />}>
                          <ProtectedRoute>
                          <OrderConfirmation />
                          </ProtectedRoute>
                        </Suspense>
                      </LazyLoadErrorBoundary>
                    } />
                    <Route path="/bundle-order-confirmation" element={
                      <LazyLoadErrorBoundary fallbackPath="/bundles">
                        <Suspense fallback={<PageLoadingFallback />}>
                          <ProtectedRoute>
                          <OrderConfirmation />
                          </ProtectedRoute>
                        </Suspense>
                      </LazyLoadErrorBoundary>
                    } />
                    <Route path="/orders" element={
                      <ProtectedRoute>
                        <OrderHistory />
                      </ProtectedRoute>
                    } />
                    <Route path="/order-details/:orderId" element={
                      <ProtectedRoute>
                        <OrderDetails />
                      </ProtectedRoute>
                    } />
                    <Route path="/about" element={<About />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/settings" element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    } />
                    <Route path="/auth/action" element={<AuthAction />} />
                    <Route path="/affiliate/signup" element={<AffiliateSignup />} />
                    <Route path="/affiliate" element={
                      <ProtectedRoute>
                        <AffiliateDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/rushd/pricing" element={<RushdPricing />} />
                    <Route path="/rushd/success" element={<RushdSuccess />} />
                    <Route path="/rushd/cancelled" element={<RushdCancelled />} />
                    {/* Catch-all route for 404 */}
                    <Route path="*" element={<NotFound />} />
                  </Route>
                  </Routes>
                    </AffiliateProvider>
                  </RushdMembershipProvider>
                </RushdSettingsProvider>
              </UserProfileProvider>
            </CartProvider>
          </ProductProvider>
        </ShippingAddressProvider>
      </ShippingProvider>
    </BannerProvider>
  );
};

const queryClient = new QueryClient();

const App = () => (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <GoogleAdsConsent />
        <AppContent />
      </TooltipProvider>
    </QueryClientProvider>
  </BrowserRouter>
);

export default App;
